/**
 * Country Utilities
 * Helper functions for country code handling and formatting.
 */

/**
 * Convert ISO 3166-1 alpha-2 code to flag emoji
 * Works by converting letters to regional indicator symbols
 * 
 * @example getCountryFlag('VN') // 🇻🇳
 * @example getCountryFlag('US') // 🇺🇸
 */
export function getCountryFlag(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) return '🌐'

    const code = countryCode.toUpperCase()

    // Special handling for "Unknown" country (XX is ISO 3166-1 user-assigned)
    if (code === 'XX') return '❓'

    const offset = 127397 // Offset to regional indicator symbols

    try {
        return String.fromCodePoint(
            code.charCodeAt(0) + offset,
            code.charCodeAt(1) + offset
        )
    } catch {
        return '🌐'
    }
}

/**
 * Format number with locale-aware separators
 * @example formatCount(1500) // "1,500"
 */
export function formatCount(count: number): string {
    return count.toLocaleString()
}

/**
 * Get short display for country - flag + code
 * @example getCountryShort('VN') // "🇻🇳 VN"
 */
/**
 * Normalize text for search by removing diacritical marks (accents)
 * This enables fuzzy matching: "Turkiye" will match "Türkiye"
 * 
 * @example normalizeSearchText('Türkiye') // "Turkiye"
 * @example normalizeSearchText('São Paulo') // "Sao Paulo"
 * @example normalizeSearchText('Côte d\'Ivoire') // "Cote d'Ivoire"
 */
export function normalizeSearchText(text: string): string {
    if (!text) return ''
    // NFD: Decompose characters (e.g., "ü" → "u" + combining diaeresis)
    // Then remove all combining diacritical marks (Unicode range 0300-036f)
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function getCountryShort(countryCode: string): string {
    return `${getCountryFlag(countryCode)} ${countryCode.toUpperCase()}`
}
